import { useCallback, useEffect, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import { Alert, Box, Button, CircularProgress, FormControlLabel, MenuItem, Stack, Switch, Tab, Tabs, TextField } from '@mui/material';
import { menuLabel } from '../menuConfig';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { useHasPermission } from '../PermissionGate';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { AssignDialog, AssignmentsTable, EndAssignmentDialog, HistoryTable, ReasonDialog } from './AssignmentParts';
import { ImportTab } from './ImportTab';
import { TypesTab } from './TypesTab';
import { ValuesTab } from './ValuesTab';
import {
  FEATURE_FUEL_SURCHARGES,
  fetchAssignments,
  fetchHistory,
  fetchTypes,
  withdrawAssignment,
  type FuelSurchargeAssignment,
  type FuelSurchargeChange,
  type FuelSurchargeType,
} from './fuelSurchargesApi';

/**
 * „Korekty paliwowe” (makieta back office v2, fala B, B4; Odpowiedzi CS
 * 20.10.2026 pkt 4; BR-ADM-04, BR-CS-12; decyzja D8 z 20.10.2026;
 * przełącznik `fuelSurcharges`, S-21.10-05).
 *
 * Kartoteka w pięciu zakładkach: rodzaje korekt, wartości w okresach,
 * przypisania kontrahentów, plik od CS i historia zmian. Zmienia przyjęcie
 * zleceń (uprawnienie „Przyjęcie zleceń”; role CS i Administracji, gdy je
 * dostaną) — każda zmiana z powodem i w historii; reszta ogląda. Na
 * zleceniu korekta jest tylko informacją — nie zmienia marży ani kosztu.
 */

export const FEATURE_OFF_NOTE = 'Ekran „Korekty paliwowe” jest wyłączony w wersji aplikacji, z której korzystasz.';

export const INTRO_NOTE =
  'Rodzaje korekt to wartości robocze od CS — do potwierdzenia i uzupełnienia przez CS: czego CS nie podał (np. okres i moment korekt Marathonu), jest „nieustalone”. Wartości korekt przyjdą od CS; dopóki ich nie ma, zlecenie pokazuje „brak — czeka na wartości od CS”, nigdy zero. Kontrahent ma jedną korektę naraz. Na zleceniu korekta jest tylko informacją — nie zmienia marży ani kosztu.';

const TABS = ['types', 'values', 'assignments', 'import', 'history'] as const;
type TabKey = (typeof TABS)[number];
const TAB_LABELS: Record<TabKey, string> = {
  types: 'Rodzaje korekt',
  values: 'Wartości',
  assignments: 'Przypisania kontrahentów',
  import: 'Plik od CS',
  history: 'Historia zmian',
};

export const FuelSurchargeScreen = () => {
  const on = useFeatureState(FEATURE_FUEL_SURCHARGES);
  if (on === undefined) return <FeaturesLoading />;
  if (!on) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title={menuLabel('fuel-surcharges')} />
        <Alert severity="info">{FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return <Screen />;
};

const AssignmentsTab = ({
  types,
  canEdit,
  reloadKey,
}: {
  types: FuelSurchargeType[];
  canEdit: boolean;
  reloadKey: number;
}) => {
  const notify = useNotify();
  const [typeId, setTypeId] = useState<number | ''>('');
  const [withdrawn, setWithdrawn] = useState(false);
  const [rows, setRows] = useState<FuelSurchargeAssignment[] | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [ending, setEnding] = useState<FuelSurchargeAssignment | null>(null);
  const [withdrawing, setWithdrawing] = useState<FuelSurchargeAssignment | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await fetchAssignments({ typeId: typeId === '' ? undefined : typeId, withdrawn }));
    } catch (e) {
      setRows([]);
      notify(toErrorMessage(e), { type: 'error' });
    }
  }, [typeId, withdrawn, notify]);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  return (
    <>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Rodzaj korekty"
          value={typeId}
          onChange={(e) => setTypeId(e.target.value === '' ? '' : Number(e.target.value))}
          sx={{ minWidth: 320 }}
        >
          <MenuItem value="">wszystkie</MenuItem>
          {types.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={<Switch checked={withdrawn} onChange={(e) => setWithdrawn(e.target.checked)} />}
          label="Pokaż wycofane"
        />
        {canEdit && (
          <Button variant="outlined" onClick={() => setAssigning(true)} sx={{ ml: 'auto' }}>
            Przypisz kontrahenta
          </Button>
        )}
      </Stack>
      {rows === null ? (
        <CircularProgress size={24} />
      ) : (
        <AssignmentsTable rows={rows} canEdit={canEdit} showContractor onEnd={setEnding} onWithdraw={setWithdrawing} />
      )}
      <AssignDialog
        open={assigning}
        onClose={() => setAssigning(false)}
        onSaved={() => {
          setAssigning(false);
          void load();
        }}
        types={types}
      />
      <EndAssignmentDialog
        assignment={ending}
        onClose={() => setEnding(null)}
        onSaved={() => {
          setEnding(null);
          void load();
        }}
      />
      <ReasonDialog
        open={withdrawing !== null}
        title="Wycofaj przypisanie"
        text="Wycofanie to poprawka pomyłki — przypisanie zostaje w historii, ale przestaje obowiązywać."
        confirmLabel="Wycofaj"
        onClose={() => setWithdrawing(null)}
        onConfirm={async (reason) => {
          if (!withdrawing) return;
          try {
            await withdrawAssignment(withdrawing.id, reason);
            notify('Przypisanie wycofane', { type: 'success' });
            setWithdrawing(null);
            void load();
          } catch (e) {
            notify(toErrorMessage(e), { type: 'error' });
          }
        }}
      />
    </>
  );
};

const Screen = () => {
  const notify = useNotify();
  const canEdit = useHasPermission('ORDER_INTAKE');
  const [tab, setTab] = useState<TabKey>('types');
  const [types, setTypes] = useState<FuelSurchargeType[] | null>(null);
  const [history, setHistory] = useState<FuelSurchargeChange[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  const loadTypes = useCallback(async () => {
    try {
      setTypes(await fetchTypes());
    } catch (e) {
      setTypes([]);
      notify(toErrorMessage(e), { type: 'error' });
    }
  }, [notify]);

  useEffect(() => {
    void loadTypes();
  }, [loadTypes]);

  useEffect(() => {
    if (tab !== 'history') return;
    void fetchHistory()
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [tab]);

  const typeName = (id: number | null) => types?.find((t) => t.id === id)?.name ?? (id ? `#${id}` : '—');

  return (
    <Box sx={{ p: 2 }} data-testid="fuel-surcharges-screen">
      <Title title={menuLabel('fuel-surcharges')} />
      <Alert severity="info" sx={{ mb: 2 }} data-testid="fuel-surcharges-intro">
        {INTRO_NOTE}
      </Alert>
      <Tabs value={tab} onChange={(_e, v: TabKey) => setTab(v)} variant="scrollable" sx={{ mb: 2 }}>
        {TABS.map((t) => (
          <Tab key={t} value={t} label={TAB_LABELS[t]} />
        ))}
      </Tabs>
      {types === null ? (
        <CircularProgress size={24} />
      ) : tab === 'types' ? (
        <TypesTab types={types} canEdit={canEdit} onChanged={() => void loadTypes()} />
      ) : tab === 'values' ? (
        <ValuesTab types={types} canEdit={canEdit} />
      ) : tab === 'assignments' ? (
        <AssignmentsTab types={types} canEdit={canEdit} reloadKey={reloadKey} />
      ) : tab === 'import' ? (
        <ImportTab
          canEdit={canEdit}
          onApplied={() => {
            setReloadKey((k) => k + 1);
            void loadTypes();
          }}
        />
      ) : (
        <HistoryTable rows={history} typeName={typeName} />
      )}
    </Box>
  );
};
